<template>
  <li :class="['crafting-material', { 'crafting-material-not-enough': !isEnoughTokens }]" @click="handleClick">
    <div class="crafting-material-content">
      <img :alt="token.label" :src="tokens[token.name.contract as Tokens].toString()" class="crafting-material-img" />
      <div v-if="!isEnoughTokens" class="crafting-material-icon-container">
        <div v-if="expeditionLocation" class="crafting-material-img-container">
          <img alt="expedition" src="/img/crafting/expeditionLocationIcon.svg" />
        </div>
        <div v-else-if="!isSellable && !hasExtraRewardMatch" class="crafting-material-img-container">
          <img alt="hammer" src="/img/crafting/ingredientCraftIcon.svg" />
        </div>
        <div v-else-if="isSellable || hasExtraRewardMatch" class="crafting-material-img-container">
          <img alt="shopping" src="/img/crafting/sellerOrMarketIcon.svg" />
        </div>
      </div>
    </div>
    <div class="crafting-material-amount">
      <span class="crafting-material-amount-available">{{ availableTokensFormatted }}</span>
      <span ref="amountSpan" class="crafting-material-amount-required">{{
        BigNumber(ingredient.quantity).multipliedBy(produceAmount).toNumber().toLocaleString('ru-RU')
      }}</span>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { useTokensReader } from '#imports';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { computed, defineProps, ref } from 'vue';
import useEnvs from '~/composables/useEnvs';
import useRecipesResponseReformatter from '~/composables/useRecipesResponseReformatter';
import tokens from '~/public/img/tokens';
import type { ITokenModel } from '~/types/apiService';
import type { RecipeIngredient, RecipeProps } from '~/types/crafting';
import { HalloweenItem } from '~/types/halloween/items';
import type { HalloweenLocation } from '~/types/halloween/location';
import type { SellerItem } from '~/types/seller';

import { tokenMetaData } from '~/utils';
import { type Tokens, tokensConfig } from '~/utils/constants';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';

const { ingredient, produceAmount } = defineProps<{
  ingredient: RecipeIngredient;
  produceAmount: number;
}>();

const emit = defineEmits<{
  notEnough: [value: boolean];
}>();

const { blockchain, apiUrl } = useEnvs();
const { isMobile } = useDevice();
const { address } = useWeb3ModalAccount();
const { availableTokens } = useTokensReader();
const store = useMainStore();
const currentlyAvailableTokens = ref<number>(0);
const isEnoughTokens = computed(() =>
  BigNumber(currentlyAvailableTokens.value).isGreaterThanOrEqualTo(
    BigNumber(ingredient.quantity).multipliedBy(produceAmount)
  )
);
const isTokenBalanceLoading = ref<boolean>(true);
const buildingHash = ref<string | null>(null);
const amountSpan = ref<HTMLElement | null>(null);
const { reformatRecipesResponse } = useRecipesResponseReformatter();

const halloween = useHalloweenStore();

const token = tokenMetaData(blockchain.contracts, ingredient.tokenAddress);
const { data: tokensData } = useNuxtData<ITokenModel[]>('all-tokens');

const goToBuildingByHash = inject<((hash: string) => void) | null>('goToBuildingByHash', null); //  open crafting building for map
const goToBuildingByKey = inject<((key: string) => void) | null>('goToBuildingByKey', null); //  open building by key (for Halloween locations)
const goToMiningBuildingByHash = inject<((hash: string) => void) | null>('goToMiningBuildingByHash', null); //  open mining building for map
const closeModal = inject<(() => void) | null>('closeModal', null);

const goToMiningBuildingByHashMobile = inject<((hash: string) => void) | null>('goToMiningBuildingByHashMobile', null); //  open mining building by mobile
const goToMiningBuildingByHashMobileFromDistricts = inject<((hash: string) => void) | null>(
  'goToMiningBuildingByHashMobileFromDistricts',
  null
); //  open mining building from /districts by mobile
const goToBuildingByHashMobile = inject<((hash: string) => void) | null>('goToBuildingByHashMobile', null); //  open crafting building by mobile
const goToBuildingByHashMobileFromDistricts = inject<((hash: string) => void) | null>(
  'goToBuildingByHashMobileFromDistricts',
  null
); //  open crafting building from /districts by mobile

const sellerData = inject<Ref<SellerItem[] | null>>('sellerData', ref(null));

const sellerIsOpen = inject<Ref<boolean> | null>('sellerIsOpen', null);
const showSellerModal = inject<Ref<boolean> | null>('showSellerModal', null);

const { data: buildingsData } = useFetch<{ buildingHashes: string[] }>('/crafting/buildings-hashes', {
  baseURL: apiUrl,
  query: {
    productAddress: ingredient.tokenAddress
  }
});

store.$onAction(async ({ name }) => {
  if (name === 'updateVersion') {
    currentlyAvailableTokens.value = address?.value
      ? Number(await availableTokens(address?.value, ingredient.tokenAddress))
      : 0;
  }
});

const adjustFontSize = async () => {
  const element = amountSpan.value;
  if (!element) return;

  element.style.fontSize = '15px';

  await nextTick();

  let currentFontSize = 15;
  const minFontSize = 8;

  while (element.scrollWidth > element.clientWidth && currentFontSize > minFontSize) {
    currentFontSize--;
    element.style.fontSize = `${currentFontSize}px`;
  }
};

async function openCraftingModal() {
  if (sellerIsOpen?.value && isIngredientAvailableInSeller.value && showSellerModal) {
    showSellerModal.value = true;
    return;
  }

  const hashes = buildingsData.value?.buildingHashes;

  if (!hashes || hashes.length === 0) return;

  if (hashes.length === 1) {
    buildingHash.value = hashes[0];
    navigateToBuilding(buildingHash.value);
    return;
  }

  const targetToken = ingredient.tokenAddress.toLowerCase();

  for (const hash of hashes) {
    const recipes = await $fetch<RecipeProps[]>(`/crafting/buildings/${hash}/recipes`, {
      baseURL: apiUrl
    });

    const found = reformatRecipesResponse(recipes).find(
      (recipe) => recipe.isActive && recipe.products.some((p) => p.tokenAddress.toLowerCase() === targetToken)
    );

    if (found) {
      buildingHash.value = hash;

      navigateToBuilding(buildingHash.value);
      return;
    }
  }
}

function navigateToBuilding(hash: string) {
  if (!hash) {
    return;
  }

  if (!isMobile) {
    goToBuildingByHash?.(hash);
    return;
  }

  goToBuildingByHashMobileFromDistricts?.(hash);
  goToBuildingByHashMobile?.(hash);
}
function openMiningBuilding() {
  if (!isMobile) {
    goToMiningBuildingByHash?.(token.name.contract);
    return;
  }

  goToMiningBuildingByHashMobileFromDistricts?.(token.name.contract);
  goToMiningBuildingByHashMobile?.(token.name.contract);
}

watchImmediate(address, async () => {
  const totalTokensOnBalance = address?.value ? await availableTokens(address?.value, ingredient.tokenAddress) : 0;
  isTokenBalanceLoading.value = false;
  currentlyAvailableTokens.value = +totalTokensOnBalance;
});

watch(
  isEnoughTokens,
  (value) => {
    emit('notEnough', !value);
  },
  { immediate: true }
);

watch(() => produceAmount, adjustFontSize);

const hasExtraRewardMatch = computed(() => {
  return Object.values(tokensConfig).some((item: any) => {
    return item.extraReward?.tokenName === token.name.contract;
  });
});

const isIngredientAvailableInSeller = computed(() => {
  if (!sellerData.value) return false;
  return sellerData?.value.some(
    (item) => item.tokenAddress.toLowerCase() === ingredient.tokenAddress.toLowerCase() && item.amount >= 1
  );
});

const clickAction = computed(() => {
  if (!isEnoughTokens.value) {
    if (!isSellable.value && !hasExtraRewardMatch.value) {
      return 'craft';
    } else if (isSellable.value || hasExtraRewardMatch.value) {
      return 'mine';
    }
  }
  return null;
});

const expeditionLocation = computed(() => {
  const overrideHalloweenTokens = [
    HalloweenItem.Minerals,
    HalloweenItem.Mushrooms,
    HalloweenItem.Roots,
    HalloweenItem.Resin,
    HalloweenItem.Bat,
    HalloweenItem.Bones,
    HalloweenItem.Flowers,
    HalloweenItem.Mahogany,
    HalloweenItem.DeadWater
  ].map((item) => halloweenItemRegistry.get(item));

  if (overrideHalloweenTokens.some((i) => i.address.toLowerCase() === ingredient.tokenAddress.toLowerCase())) {
    const location: HalloweenLocation = [
      HalloweenItem.Minerals,
      HalloweenItem.DeadWater,
      HalloweenItem.Bones,
      HalloweenItem.Bat
    ].some((i) => halloweenItemRegistry.get(i).address.toLowerCase() === ingredient.tokenAddress.toLowerCase())
      ? 'cave'
      : 'forest';

    return location;
  }

  return null;
});

function handleClick() {
  if (expeditionLocation.value) {
    const targetLocation = expeditionLocation.value;

    if (!isMobile) {
      goToBuildingByKey?.(targetLocation);
    }

    setTimeout(() => {
      closeModal?.();
      halloween.handleExpeditionLocationSelect(targetLocation);
    }, 1300);

    buildingHash.value = null;
  } else if (clickAction.value === 'craft') {
    openCraftingModal();
  } else if (clickAction.value === 'mine') {
    openMiningBuilding();
  }
}

const matchedToken = computed(() => {
  if (!tokensData.value || !ingredient.tokenAddress) return null;
  return tokensData.value.find((t: ITokenModel) => t.tokenContractAddress === ingredient.tokenAddress) || null;
});

const isSellable = computed(() => !!matchedToken.value?.isSellable);

const availableTokensFormatted = computed(() => {
  const absNum = Math.abs(currentlyAvailableTokens.value);

  if (absNum >= 1e6) {
    return (currentlyAvailableTokens.value / 1e6).toFixed(1).replace(/\.?0+$/, '') + 'M';
  } else if (absNum >= 1e3) {
    return (currentlyAvailableTokens.value / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'k';
  } else {
    return BigNumber(currentlyAvailableTokens.value).integerValue(BigNumber.ROUND_DOWN);
  }
});

onMounted(adjustFontSize);
</script>

<style lang="scss" scoped>
.crafting-material {
  position: relative;
  padding: 0;
  width: 90px;
  font-weight: 600;
  font-size: 16px;
  font-family: Eczar, sans-serif;

  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  background: #041a27;
  border: 1px solid #1e353e;
  border-radius: 6px;
  box-shadow: 2.08px 4.17px 5.21px 0px rgba(0, 0, 0, 0.25);

  &-content {
    display: flex;
    align-items: center;
  }

  &-icon-container {
    border: 0.82px solid #ff770066;
    border-radius: 5px;
    width: 36px;
    height: 36px;
    margin-right: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: linear-gradient(180deg, #01080a 0%, #153b47 100%);
    cursor: pointer;

    &:hover {
      background: radial-gradient(89.68% 100% at 50% 100%, #2b594c 0%, #020b0e 100%),
        linear-gradient(180deg, rgba(1, 8, 10, 0.9) 0%, rgba(21, 59, 71, 0.9) 100%);
    }
  }

  &-img-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &-img {
    height: 48px;
    width: 48px;
  }

  &-amount {
    width: 100%;
    display: flex;
    height: 24px;
    border-top: 1px solid #1e353e;

    span {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &-amount-available {
    color: #ffffff80;
    font-size: 13px;
    border-right: 1px solid #1e353e;
    border-bottom-left-radius: 4px;
  }

  &-amount-required {
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    width: 45px;
    color: white;
    border-bottom-right-radius: 4px;
    font-size: 15px;
    background: linear-gradient(180deg, #01080a 0%, #153b47 100%),
      radial-gradient(84.07% 93.75% at 52.63% 100%, #2b594c 0%, rgba(26, 53, 45, 0) 100%);
  }

  &.crafting-material-not-enough {
    box-shadow: 0px 0px 6px 0px #ce1b49bf;

    .crafting-material-amount-available {
      color: #f6164c;
      background: radial-gradient(89.68% 100% at 52.63% 100%, rgba(246, 22, 76, 0.3) 0%, rgba(246, 22, 76, 0) 100%),
        linear-gradient(180deg, rgba(1, 8, 10, 1) 0%, rgba(21, 59, 71, 1) 100%);
    }
  }
}
</style>
